import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../api/services';
import {
  Document,
  TypeDocument,
  TYPE_DOCUMENT_LABELS,
  STATUT_PAIEMENT_LABELS,
  Page,
} from '../../types';
import { formatTND, formatDate } from '../../utils/formatters';
import { DataTable, Column } from '../../components/common/DataTable';
import { StatusBadge, DocumentTypeBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

const TYPE_FROM_PATH: Record<string, TypeDocument> = {
  factures: TypeDocument.FACTURE,
  devis: TypeDocument.DEVIS,
  avoirs: TypeDocument.AVOIR,
  commandes: TypeDocument.COMMANDE,
  'bons-livraison': TypeDocument.BON_LIVRAISON,
  'bons-commande': TypeDocument.BON_COMMANDE,
  'factures-achat': TypeDocument.FACTURE,
};

// Sens à appliquer par slug : les vues d'achat filtrent les ACHATS.
const SENS_FROM_PATH: Record<string, 'VENTE' | 'ACHAT'> = {
  'bons-commande': 'ACHAT',
  'factures-achat': 'ACHAT',
};

export function DocumentList() {
  const { t } = useLanguage();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const typeDocument = type ? TYPE_FROM_PATH[type] : undefined;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState<string>('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const pageTitle = typeDocument
    ? TYPE_DOCUMENT_LABELS[typeDocument] + 's'
    : t.documents.title;

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, size: 10 };
      if (typeDocument) params.typeDocument = typeDocument;
      if (type && SENS_FROM_PATH[type]) params.sens = SENS_FROM_PATH[type];
      if (search) params.search = search;
      if (statutFilter) params.statutPaiement = statutFilter;
      if (dateDebut) params.dateDebut = dateDebut;
      if (dateFin) params.dateFin = dateFin;
      const data: Page<Document> = await documentService.getAll(params);
      setDocuments(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      toast.error(t.messages.loadError);
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeDocument, search, statutFilter, dateDebut, dateFin, t.messages.loadError]);

  useEffect(() => {
    setCurrentPage(0);
  }, [type]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await documentService.delete(deleteId);
      toast.success(t.messages.deleteSuccess);
      loadDocuments();
    } catch (error) {
      toast.error(t.messages.deleteError);
    } finally {
      setDeleteId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadDocuments();
  };

  const columns: Column<Document>[] = [
    {
      key: 'numero',
      header: t.documents.numero,
      sortable: true,
      render: (doc) => (
        <span className="font-medium text-primary-600">{doc.numero}</span>
      ),
    },
    ...(!typeDocument
      ? [
          {
            key: 'typeDocument',
            header: t.documents.typeDocument,
            render: (doc: Document) => <DocumentTypeBadge type={doc.typeDocument} />,
          },
        ]
      : []),
    {
      key: 'dateDocument',
      header: t.documents.dateDocument,
      sortable: true,
      render: (doc) => (
        <span className="text-sm text-gray-600">{formatDate(doc.dateDocument)}</span>
      ),
    },
    {
      key: 'clientRaisonSociale',
      header: (type && SENS_FROM_PATH[type] === 'ACHAT') ? 'Fournisseur' : t.documents.client,
      sortable: true,
      render: (doc) => (
        <span className="text-sm font-medium text-gray-900">
          {doc.sensDocument === 'ACHAT'
            ? (doc.fournisseurRaisonSociale ?? doc.clientRaisonSociale)
            : doc.clientRaisonSociale}
        </span>
      ),
    },
    {
      key: 'netAPayer',
      header: t.documents.netAPayer,
      sortable: true,
      render: (doc) => (
        <span className="tnd-amount font-medium">{formatTND(doc.netAPayer)}</span>
      ),
    },
    {
      key: 'co2eKg',
      header: 'CO₂e',
      render: (doc) =>
        doc.co2eKg != null && doc.co2eKg > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium px-2 py-0.5 border border-emerald-100">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
              <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3c.5.12 1 .2 1.5.2C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
            </svg>
            {doc.co2eKg.toLocaleString('fr-TN', { maximumFractionDigits: 1 })} kg
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'statutPaiement',
      header: t.documents.statutPaiement,
      render: (doc) => <StatusBadge statut={doc.statutPaiement} />,
    },
    {
      key: 'actions',
      header: t.common.actions,
      render: (doc) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/documents/view/${doc.id}`);
            }}
            className="text-gray-600 hover:text-primary-600 p-1"
            title="Voir"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                doc.sensDocument === 'ACHAT'
                  ? `/stock/achats/${doc.id}/edit`
                  : `/documents/${doc.id}/edit`,
              );
            }}
            className="text-primary-600 hover:text-primary-800 p-1"
            title={t.common.edit}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(doc.id);
            }}
            className={`text-red-600 hover:text-red-800 p-1 ${hasPermission('DOCUMENTS:DELETE') ? '' : 'hidden'}`}
            title={t.common.delete}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{pageTitle}</h1>
          <p className="page-subtitle">{totalElements} documents</p>
        </div>
        <button
          onClick={() =>
            navigate(
              type === 'factures-achat'
                ? '/stock/achats/new'
                : `/documents/new${typeDocument ? `?type=${typeDocument}` : ''}`,
            )
          }
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          {t.documents.newDocument}
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={`${t.common.search} (numéro, client)...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            <select
              value={statutFilter}
              onChange={(e) => {
                setStatutFilter(e.target.value);
                setCurrentPage(0);
              }}
              className="input-field sm:w-48"
            >
              <option value="">{t.common.all} - {t.common.status}</option>
              {Object.entries(STATUT_PAIEMENT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500 whitespace-nowrap">Du :</label>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500 whitespace-nowrap">Au :</label>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                className="input-field"
              />
            </div>
            <button type="submit" className="btn-primary">
              {t.common.filter}
            </button>
          </div>
        </form>
      </div>

      <DataTable
        columns={columns}
        data={documents}
        totalElements={totalElements}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading}
        emptyMessage={t.common.noResults}
        onRowClick={(doc) => navigate(`/documents/view/${doc.id}`)}
      />

      <ConfirmDialog
        isOpen={deleteId !== null}
        title={t.messages.confirmDelete}
        message={t.documents.deleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

export default DocumentList;
